import { normalizeColorHex } from '@/lib/utils';
import { Currency } from '@/modules/common';

import {
  GeneratedInvoiceOverheadItemResponse,
  GeneratedInvoiceTeamMemberResponse,
  GeneratedProjectInvoiceResponse,
  GetGeneratedInvoicesResponse,
} from '../types/dto/response';
import {
  GenerateInvoiceListItem,
  GenerateInvoiceProjectOverheadListItem,
  GenerateInvoiceProjectTeamMemberListItem,
  GenerateInvoiceSubRowType,
  InvoiceHoursSourceType,
} from '../types/generate-invoices';
import { mapInvoiceHoursSourcesFromDTO } from './invoice-hours-source.mapper';

/**
 * Maps team member invoice data from API response to domain model
 */
const mapTeamMemberFromResponse = (
  memberResponse: GeneratedInvoiceTeamMemberResponse,
  currencyCode: string
): GenerateInvoiceProjectTeamMemberListItem => {
  const hoursSources = mapInvoiceHoursSourcesFromDTO(memberResponse.hoursSources);

  const primarySource =
    hoursSources.find(s => s.sourceType === InvoiceHoursSourceType.TRACKED_BILLABLE) ||
    hoursSources[0];

  return {
    type: GenerateInvoiceSubRowType.MEMBER,
    id: memberResponse.member.id.toString(),
    name: memberResponse.member.name,
    avatarUrl: memberResponse.member.profileImageUrl || '',
    invoiceBy: primarySource?.sourceType || InvoiceHoursSourceType.TRACKED_BILLABLE,
    hours: primarySource?.hours || 0,
    effectiveRate: primarySource?.averageRate || 0,
    currency: {
      code: currencyCode,
      displayName: '',
    } as Currency,
    total: primarySource?.amount || 0,
    hoursSources,
  };
};

/**
 * Maps a scheduled (PENDING income) overhead from API response to the same
 * domain shape used by user-added ad-hoc overhead lines. The presence of
 * `overheadId` is what distinguishes a scheduled row from an ad-hoc one.
 */
const mapScheduledOverheadFromResponse = (
  overheadResponse: GeneratedInvoiceOverheadItemResponse,
  currencyCode: string
): GenerateInvoiceProjectOverheadListItem => ({
  type: GenerateInvoiceSubRowType.OVERHEAD,
  id: overheadResponse.overheadId.toString(),
  description: overheadResponse.description,
  effectiveRate: Number(overheadResponse.amount),
  currency: {
    code: currencyCode,
    displayName: '',
  } as Currency,
  overheadId: overheadResponse.overheadId,
  date: overheadResponse.date,
});

/**
 * Maps project invoice data from API response to domain model
 */
const mapProjectFromResponse = (
  projectResponse: GeneratedProjectInvoiceResponse
): GenerateInvoiceListItem => {
  const projectMembers = projectResponse.members.map(member =>
    mapTeamMemberFromResponse(member, projectResponse.currencyCode)
  );

  const scheduledOverheads = (projectResponse.overheads ?? []).map(overhead =>
    mapScheduledOverheadFromResponse(overhead, projectResponse.currencyCode)
  );

  const totalHours = projectMembers.reduce((sum, member) => sum + member.hours, 0);
  const totalAmount = projectMembers.reduce((sum, member) => sum + member.total, 0);
  const avgRate = totalHours > 0 ? totalAmount / totalHours : 0;

  return {
    id: projectResponse.project.id.toString(),
    invoiceBy: '',
    hours: 0,
    effectiveRate: avgRate,
    currency: {
      code: projectResponse.currencyCode,
      displayName: '',
    },
    total: totalAmount,
    project: {
      id: projectResponse.project.id,
      name: projectResponse.project.name,
      clientName: projectResponse.clientName,
      billingModel: projectResponse.project.billingModel,
      colorHex: normalizeColorHex(projectResponse.project.colorHex) ?? '',
      logoUrl: projectResponse.project.logoUrl || '',
      warnings: projectResponse.warnings,
    },
    projectMembers,
    projectAddedMembers: [],
    projectOverheads: scheduledOverheads,
    projectSubRows: [...projectMembers, ...scheduledOverheads],
  };
};

/**
 * Maps generated invoices response from API to domain models
 *
 * @param response - API response from GET /api/invoices/generated
 * @returns Array of GenerateInvoiceListItem for the UI table
 */
export const mapGeneratedInvoicesFromResponse = (
  response: GetGeneratedInvoicesResponse
): GenerateInvoiceListItem[] => {
  return response.projects.map(mapProjectFromResponse);
};
